import { useMemo, useState } from "react";
import { useAuthContext } from "../../context/auth";
import { esBrokerAdLab } from "../../utils/grupoBroker";
import { matchAgenciaFilter } from "..";
import "./stylesListaClientesPorEjecutivo.scss";

// Badge de tipo de cliente (categoria_cliente). null → sin badge (se muestra "—").
const tipoBadgeClass = (tipo) => {
  switch (tipo) {
    case "A":
      return "bg-success";
    case "B":
      return "bg-warning text-dark";
    case "C":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const RAYA = "—";

// Case/acento-insensitive para el buscador de texto libre.
const normalizar = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Recibe filas ya resueltas (nombre↔id) y los valores de filtro desde el page.
// Responsabilidad única: filtrar → ordenar → renderizar la tabla.
export const ListaClientesPorEjecutivo = ({ filas = [], hayBrokers = false, filtros = {} }) => {
  const {
    busqueda = "",
    filtroAgencia = "",
    filtroCategoria = "",
    filtroSubcategoria = "",
    filtroTipo = "",
    filtroEjecutivo = "",
    filtroBroker = "",
  } = filtros;

  const { pais } = useAuthContext();
  // Orden por defecto: alfabético por Cliente (asc). Mismo patrón que VentasDetalle.
  const [sortConfig, setSortConfig] = useState({ key: "cliente", direction: "asc" });

  // Filtros combinables (AND). Agencia/Ejecutivo/Broker por id (regla 10.7);
  // el buscador matchea por nombre resuelto.
  const filasFiltradas = useMemo(() => {
    const q = normalizar(busqueda);
    return filas.filter((f) => {
      if (q) {
        const heno = normalizar([f.cliente, f.categoria, f.ejecutivo, f.agencia, f.broker].join(" "));
        if (!heno.includes(q)) return false;
      }
      if (!matchAgenciaFilter(filtroAgencia, f.agenciaId, pais)) return false;
      if (filtroCategoria && String(f.categoriaId) !== String(filtroCategoria)) return false;
      if (filtroSubcategoria && String(f.subcategoriaId) !== String(filtroSubcategoria)) return false;
      if (filtroTipo && f.tipo !== filtroTipo) return false;
      if (filtroEjecutivo && String(f.ejecutivoId) !== String(filtroEjecutivo)) return false;
      // Filtro de grupo (Perú): AdLab = cuenta broker_id=115; Visiona = el complemento (no AdLab).
      // El fila usa `brokerId` (camelCase); se adapta al shape que espera esBrokerAdLab.
      if (filtroBroker === "grupo:adlab" && !esBrokerAdLab({ broker_id: f.brokerId })) return false;
      if (filtroBroker === "grupo:visiona" && esBrokerAdLab({ broker_id: f.brokerId })) return false;
      return true;
    });
  }, [
    filas,
    busqueda,
    filtroAgencia,
    filtroCategoria,
    filtroSubcategoria,
    filtroTipo,
    filtroEjecutivo,
    filtroBroker,
    pais,
  ]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Orden de aplicación: filtros → sort → render.
  const filasSorted = useMemo(() => {
    const arr = [...filasFiltradas];
    if (!sortConfig.key) return arr;
    const direction = sortConfig.direction === "asc" ? 1 : -1;
    return arr.sort((a, b) => {
      const valA = a[sortConfig.key] || "";
      const valB = b[sortConfig.key] || "";
      return direction * valA.localeCompare(valB);
    });
  }, [filasFiltradas, sortConfig]);

  const columnas = [
    { key: "cliente", label: "CLIENTE" },
    { key: "categoria", label: "CATEGORÍA" },
    { key: "tipo", label: "TIPO" },
    { key: "ejecutivo", label: "EJECUTIVO" },
    { key: "agencia", label: "AGENCIA" },
    ...(hayBrokers ? [{ key: "broker", label: "BROKER" }] : []),
  ];

  return (
    <div className="cartera-table-container">
      <div className="row justify-content-center">
        <div className="col-md-12">
          <table className="table table-bordered table-striped table-hover table-sm">
            <thead>
              <tr>
                {columnas.map((col) => (
                  <th
                    key={col.key}
                    className={`col-${col.key}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}{" "}
                    {sortConfig.key === col.key && (
                      <span style={{ fontSize: "10px", opacity: 0.6 }}>
                        {sortConfig.direction === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filasSorted.length === 0 ? (
                <tr>
                  <td colSpan={columnas.length} className="text-center">
                    No hay clientes según los filtros seleccionados :(
                  </td>
                </tr>
              ) : (
                filasSorted.map((fila) => (
                  <tr key={fila.id}>
                    <td className="col-cliente">{fila.cliente || RAYA}</td>
                    <td className="col-categoria" title={fila.categoria || undefined}>
                      {fila.categoria || RAYA}
                    </td>
                    <td className="col-tipo">
                      {fila.tipo ? (
                        <span className={`badge rounded-pill cartera-tipo-badge ${tipoBadgeClass(fila.tipo)}`}>
                          {fila.tipo}
                        </span>
                      ) : (
                        RAYA
                      )}
                    </td>
                    <td className="col-ejecutivo">{fila.ejecutivo || RAYA}</td>
                    <td className="col-agencia">{fila.agencia || RAYA}</td>
                    {hayBrokers && <td className="col-broker">{fila.broker || RAYA}</td>}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
